import React, { useEffect, useState } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowsRightLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { InputNumber } from "@/components/ui/input";

import { useGetExchanges, useGetAssets, useTxn } from "@moonscape/shared";
import { CoinIcon } from "../CoinIcon";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";

const TYPES: Type[] = [
  {
    id: 1,
    name: "Trade",
    description: "This is where you exchange one or many currency for another",
    icon: (size: number) => (
      <div className="flex items-center justify-center rounded-full bg-muted" style={{ width: size, height: size }}>
        <ArrowsRightLeftIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    ),
    subTypes: ["Trade", "Buy", "Sell", "Convert", "Swap", "Loss Write-off"],
    side: { in: "Buy", out: "Sell" },
  },
  {
    id: 2,
    name: "Receive",
    description:
      "When you get crypto from things like rewards, income, or deposits",
    icon: (size: number) => (
      <div className="flex items-center justify-center rounded-full bg-muted" style={{ width: size, height: size }}>
        <ArrowDownIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    ),
    subTypes: ["Receive", "Deposit", "Airdrop", "Mining", "Staking", "Gift Received"],
    side: { in: "Receive" },
  },
  {
    id: 3,
    name: "Send",
    description:
      "When you send crypto to someone else, like for gifts or payments",
    icon: (size: number) => (
      <div className="flex items-center justify-center rounded-full bg-muted" style={{ width: size, height: size }}>
        <ArrowUpIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    ),
    subTypes: ["Send", "Withdrawal", "Gift Sent", "Donation", "Payment"],
    side: { out: "Send" },
  },
  {
    id: 4,
    name: "Transfer",
    description:
      "This is where you move funds between your own accounts or wallets",
    icon: (size: number) => (
      <div className="flex items-center justify-center rounded-full bg-muted" style={{ width: size, height: size }}>
        <ArrowRightIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    ),
    subTypes: ["Transfer"],
    side: { out: "Transfer" },
  },
];

interface Type {
  id: number;
  name: string;
  description: string;
  icon: any;
  subTypes: string[];
  side: any;
}

const AddTransactionDetail = ({
  open: openAddTransaction,
  setOpen: setOpenAddTransaction,
}) => {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const setFieldsValue = (vals: Record<string, any>) => setFormValues(prev => ({ ...prev, ...vals }));
  const [open, setOpen] = useState(false);
  const [currentType, setCurrentType] = useState<Type>(TYPES[0]);
  const [subType, setSubType] = useState<string>(TYPES[0].subTypes[0]);
  const {
    mutate: insertOrUpdateTxn,
    isPending: isPendingTxn,
    isError: isErrorTxn,
  } = useTxn({
    onSuccess: () => {
      setOpenAddTransaction(false);
      setOpen(false);
    },
    onError: () => null,
  });
  const { exchanges, isQueryLoading: isQueryLoadingExchanges } =
    useGetExchanges();

  const {
    assets,
    setSearchQuery,
    isQueryLoading: isQueryLoadingAssets,
  } = useGetAssets({ initialSearchQuery: "USD" });

  const toggleDrawer = (type: Type | null) => {
    setOpen(!open);
    if (type) {
      setCurrentType(type);
      setSubType(type?.subTypes[0] as string);
    }
    setOpenAddTransaction(!openAddTransaction);
  };

  const TYPE_TO_API: Record<string, string> = {
    "loss write-off": "loss_writeoff",
    "gift sent": "gift_sent",
    "gift received": "gift_received",
    "staking reward": "staking_reward",
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = formValues;
    const rawType = value?.type?.toLocaleLowerCase();
    const parsedTxn = {
      tradeid: uuidv4().toString().toLocaleLowerCase(),
      timestamp: new Date(value?.datetime).getTime(),
      datetime: value?.datetime?.toISOString(),
      type: TYPE_TO_API[rawType] || rawType,
      buyasset: value?.buyAsset
        ? JSON.parse(value?.buyAsset)?.symbol
        : null,
      buyqty: value?.buyQty,
      buyvalue: null,
      sellasset: value?.sellAsset
        ? JSON.parse(value?.sellAsset)?.symbol
        : null,
      sellqty: value?.sellQty,
      sellvalue: null,
      feeasset: value?.feeAsset
        ? JSON.parse(value?.feeAsset)?.symbol
        : null,
      feeqty: value?.feeQty,
      feevalue: null,
      wallet: null,
      exchange: value?.exchange,
      side: value?.type?.toLocaleLowerCase(),
      price: null,
      importtype: "user",
      ccxt_id: null,
      sellvalueasset: null,
      buyvalueasset: null,
      ext_txn_id: null,
      raw_exchange_txn_id: null,
      txn_note: null,
      deleted: false,
    };
    insertOrUpdateTxn(parsedTxn);
  };

  return (
    <div>
      <div className="flex flex-col gap-3 w-full">
        {TYPES.map((type) => (
          <div
            key={type.name}
            className="bg-secondary-bg dark:bg-card hover:bg-accent dark:hover:bg-tertiary-bg border border-border transition-colors rounded-xl cursor-pointer p-4"
            onClick={() => toggleDrawer(type)}
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center">
                {type.icon(40)}
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-lg font-semibold text-foreground">
                  {type.name}
                </span>
                <span className="text-sm text-muted-foreground">
                  {type.description}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Drawer
        title="Add New Transaction"
        placement="right"
        onClose={() => {
          toggleDrawer(null);
          setOpenAddTransaction(false);
        }}
        open={open}
        width="max-w-[600px]"
      >
        <form onSubmit={handleFormSubmit}>
          <div className="flex flex-col">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 font-semibold">
                <span className="text-foreground">Specific Transaction</span>
                <span className="text-muted-foreground">(Optional)</span>
              </div>

              <Select
                className="w-full"
                value={subType}
                onChange={(val) => {
                  setSubType(val);
                  setFieldsValue({ type: val });
                }}
                options={currentType.subTypes.map((st) => ({
                  value: st,
                  label: st,
                }))}
              />
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <div className="flex items-center gap-3 font-semibold text-foreground">
                Wallet
              </div>
              <Select
                className="w-full"
                placeholder="Select where the transaction executed"
                value={formValues.exchange}
                onChange={(val) => setFieldsValue({ exchange: val })}
                options={exchanges?.map((item) => ({
                  value: item.id,
                  label: item?.exchangename,
                })) || []}
              />
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <div className="flex items-center gap-3 font-semibold text-foreground">
                Executed time
              </div>
              <input
                type="datetime-local"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={formValues.datetime ? new Date(formValues.datetime).toISOString().slice(0, 19) : ""}
                onChange={(e) => setFieldsValue({ datetime: e.target.value ? new Date(e.target.value) : null })}
                step="1"
              />
            </div>

            {Object.keys(currentType.side).includes("out") && (
              <div className="flex flex-col gap-4 mt-4">
                <div className="flex items-center font-semibold text-foreground">
                  {`${currentType.side.out} currency`}
                </div>
                <div className="flex items-center justify-between gap-8">
                  <Select
                    className="w-full"
                    placeholder="Select the sell asset"
                    value={formValues.sellAsset}
                    onChange={(val) => setFieldsValue({ sellAsset: val })}
                    options={assets?.map((item) => ({
                      value: JSON.stringify(item),
                      label: `${item?.name} (${item?.symbol})`,
                    })) || []}
                  />
                  <InputNumber
                    className="w-full"
                    step={1e-6}
                    min={1e-6}
                    precision={6}
                    placeholder={`${currentType.side.out} quantity`}
                    value={formValues.sellQty}
                    onChange={(val) => setFieldsValue({ sellQty: val })}
                  />
                </div>
              </div>
            )}

            {Object.keys(currentType.side).includes("in") && (
              <div className="flex flex-col gap-4 mt-4">
                <div className="flex items-center font-semibold text-foreground">
                  {`${currentType.side.in} currency`}
                </div>
                <div className="flex items-center justify-between gap-8">
                  <Select
                    className="w-full"
                    placeholder="Select the buy asset"
                    value={formValues.buyAsset}
                    onChange={(val) => setFieldsValue({ buyAsset: val })}
                    options={assets?.map((item) => ({
                      value: JSON.stringify(item),
                      label: `${item?.name} (${item?.symbol})`,
                    })) || []}
                  />
                  <InputNumber
                    className="w-full"
                    step={1e-6}
                    min={1e-6}
                    precision={6}
                    placeholder={`${currentType.side.in} quantity`}
                    value={formValues.buyQty}
                    onChange={(val) => setFieldsValue({ buyQty: val })}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4 mt-4">
              <div className="flex items-center gap-2 font-semibold">
                <span className="text-foreground">Fee currency</span>
                <span className="text-muted-foreground">(Optional)</span>
              </div>
              <div className="flex items-center justify-between gap-8">
                <Select
                  className="w-full"
                  placeholder="Select the fee asset"
                  value={formValues.feeAsset}
                  onChange={(val) => setFieldsValue({ feeAsset: val })}
                  options={assets?.map((item) => ({
                    value: JSON.stringify(item),
                    label: `${item?.name} (${item?.symbol})`,
                  })) || []}
                />
                <InputNumber
                  className="w-full"
                  step={1e-6}
                  min={1e-6}
                  precision={6}
                  placeholder="Fee quantity"
                  value={formValues.feeQty}
                  onChange={(val) => setFieldsValue({ feeQty: val })}
                />
              </div>
              <div className="pt-8 flex">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isPendingTxn}
                  className="rounded-full font-semibold !bg-green-700 hover:!bg-green-800 !border-none"
                >
                  <PlusIcon className="h-5 w-5 mr-1" />
                  {isPendingTxn ? "Adding..." : "Add Transaction"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default AddTransactionDetail;
